import PropTypes from "prop-types"
import React, { useState } from "react"
import { useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import ItemDetailsModal from "src/main/Billing/Items/ItemDetailsModal"
import RemoveItemModal from "src/main/Billing/Items/RemoveItemModal"
import { formatDay } from "src/main/Billing/PaymentDetail/helpers"
import { invalidateBillingSurfaces } from "src/main/Billing/invalidateBillingSurfaces"

import DataTableV2 from "src/components/DataTableV2"
import SideSheet from "src/components/SideSheet"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import WithInvoiceActionContext from "./WithInvoiceActionContext"
import { useInvoiceTracker } from "./useInvoiceTracker"

const billingDates = ({ serviceStartOn, serviceEndOn }) =>
  serviceStartOn === serviceEndOn
    ? formatDay(serviceStartOn)
    : `${formatDay(serviceStartOn)} – ${formatDay(serviceEndOn)}`

const OpenLineModal = ({ context, openLine, onClose, onItemRemoved }) => {
  const item = context.items.find(
    (candidate) => candidate.id === openLine.txnId
  )
  if (!item) return null

  if (openLine.action === "view") {
    return <ItemDetailsModal items={[item]} isOpen onClose={onClose} />
  }

  const ids = context.job_line_items[item.id]
  return (
    <RemoveItemModal
      ledgerId={context.ledger_id}
      reservationId={context.container.reservation_id ?? undefined}
      saleId={context.container.sale_id ?? undefined}
      jobLineItem={ids && { jobId: ids.job_id, lineItemId: ids.line_item_id }}
      item={item}
      onItemRemoved={onItemRemoved}
      isOpen
      onClose={onClose}
    />
  )
}

OpenLineModal.propTypes = {
  context: PropTypes.shape({
    ledger_id: PropTypes.string.isRequired,
    container: PropTypes.object.isRequired,
    items: PropTypes.array.isRequired,
    job_line_items: PropTypes.object.isRequired,
  }).isRequired,
  openLine: PropTypes.shape({
    action: PropTypes.oneOf(["view", "remove"]).isRequired,
    txnId: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onItemRemoved: PropTypes.func.isRequired,
}

const InvoiceProductsTable = ({ invoiceId, lines, totalCents }) => {
  const { presentmentCurrency } = useMarina()
  const inSheet = SideSheet.useInSideSheet()
  const queryClient = useQueryClient()
  const [openLine, setOpenLine] = useState(null)
  const track = useInvoiceTracker()
  const close = () => setOpenLine(null)
  const closeAndReload = () => {
    close()
    invalidateBillingSurfaces(queryClient)
  }

  const money = (amount) =>
    formatCurrencyFromMinorUnits({ amount, currency: presentmentCurrency })
  // A share of a split sale has no breakdown of its own; the server sends null.
  const breakdown = (amount) => (amount == null ? "—" : money(amount))
  // As on the Items tab, a return reads as the item it takes back, struck through.
  const returnClass = (line) =>
    line.kind === "return" ? "line-through" : undefined

  const moneyColumn = {
    cellAlign: "right",
    headerAlign: "right",
    width: "120px",
  }

  const columns = [
    {
      key: "name",
      header: "Name",
      width: "260px",
      render: (line) => (
        <div className="py-2">
          <span className={returnClass(line)}>{line.name}</span>
          {inSheet && (
            <span
              className={`block text-sm text-gray-700 ${
                returnClass(line) ?? ""
              }`}
            >
              {billingDates(line)}
            </span>
          )}
          {line.partialLabel && (
            <span className="block text-sm text-gray-700">
              {line.partialLabel}
            </span>
          )}
        </div>
      ),
    },
    // A sheet is too narrow for every column, so there the dates sit under the name.
    !inSheet && {
      key: "billingDates",
      header: "Billing Dates",
      width: "220px",
      render: (line) => (
        <span className={returnClass(line)}>{billingDates(line)}</span>
      ),
    },
    {
      key: "subtotal",
      header: "Subtotal",
      ...moneyColumn,
      render: (line) => breakdown(line.subtotalCents),
    },
    {
      key: "discount",
      header: "Discount",
      ...moneyColumn,
      render: (line) => breakdown(line.discountCents),
    },
    {
      key: "tax",
      header: "Tax",
      ...moneyColumn,
      render: (line) => breakdown(line.taxCents),
    },
    {
      key: "total",
      header: "Total",
      ...moneyColumn,
      render: (line) => money(line.totalCents),
    },
  ].filter(Boolean)

  // The total row sits outside the table, so it borrows the column widths to line up under Total.
  const totalColumnIndex = columns.findIndex((column) => column.key === "total")
  const widthBeforeTotal = columns
    .slice(0, totalColumnIndex)
    .reduce((sum, column) => sum + parseInt(column.width, 10), 0)

  const actions = [
    {
      role: "view",
      label: "View details",
      icon: "eye",
      iconStyle: "solid",
      onClickSingle: (line) => {
        track("item_details_pressed")
        setOpenLine({ ...line, action: "view" })
      },
      hidden: (line) => line.kind === "return",
    },
    {
      label: "Remove",
      icon: "trash-can",
      iconStyle: "solid",
      danger: true,
      onClickSingle: (line) => {
        track("item_remove_pressed")
        setOpenLine({ ...line, action: "remove" })
      },
      hidden: (line) => !line.canRemove,
      disabled: (line) => Boolean(line.removeDisabledReason),
      disabledReason: (line) => line.removeDisabledMessage,
    },
  ]

  return (
    <section aria-labelledby="invoice-products-heading">
      <h2
        id="invoice-products-heading"
        className="mb-3 mt-0 flex items-center gap-2 text-base font-semibold text-gray-900"
      >
        <i
          className="fa-regular fa-cart-shopping text-gray-700"
          aria-hidden="true"
        />
        Products / Services
      </h2>
      <DataTableV2
        name="invoice-products-table"
        columns={columns}
        data={lines}
        actions={actions}
        hideSettings
        maxHeight="none"
        emptyState={
          <span className="text-gray-500">
            Nothing is billed on this invoice
          </span>
        }
      />
      {/* Overlaps the table's rounded bottom edge so the total reads as the frame's last row. */}
      {lines.length > 0 && (
        <div className="relative -mt-1 flex items-baseline rounded-b border bg-white py-3">
          <span
            className="shrink-0 px-4 text-sm font-semibold text-gray-900"
            style={{ width: `${widthBeforeTotal}px` }}
          >
            Grand Total
          </span>
          <span
            className="shrink-0 px-4 text-right text-lg font-semibold text-gray-900"
            style={{ width: moneyColumn.width }}
          >
            {money(totalCents)}
          </span>
        </div>
      )}
      {openLine && (
        <WithInvoiceActionContext invoiceId={invoiceId} onError={close}>
          {(context) => (
            <OpenLineModal
              context={context}
              openLine={openLine}
              onClose={close}
              onItemRemoved={closeAndReload}
            />
          )}
        </WithInvoiceActionContext>
      )}
    </section>
  )
}

InvoiceProductsTable.propTypes = {
  invoiceId: PropTypes.string.isRequired,
  lines: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      kind: PropTypes.oneOf(["sale", "return", "surcharge"]).isRequired,
      name: PropTypes.string.isRequired,
      serviceStartOn: PropTypes.string.isRequired,
      serviceEndOn: PropTypes.string.isRequired,
      subtotalCents: PropTypes.number,
      discountCents: PropTypes.number,
      taxCents: PropTypes.number,
      totalCents: PropTypes.number.isRequired,
      partialLabel: PropTypes.string,
      canRemove: PropTypes.bool.isRequired,
      removeDisabledReason: PropTypes.string,
      removeDisabledMessage: PropTypes.string,
    })
  ).isRequired,
  totalCents: PropTypes.number.isRequired,
}

export default InvoiceProductsTable
